/**
 * Theme system for the LifeScribe dashboard.
 *
 * Resolves user preference order:
 *   1. localStorage 'yvree-theme'
 *   2. prefers-color-scheme media query
 *   3. 'dark' (existing dashboard default)
 *
 * Applies the resolved theme as a data-theme attribute on <html>,
 * which CSS variables in index.css read to flip every dashboard color.
 */

export type Theme = "light" | "dark";
const STORAGE_KEY = "yvree-theme";

export function getStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

export function getSystemTheme(): Theme {
  try {
    return window.matchMedia?.("(prefers-color-scheme: light)")?.matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function resolveInitialTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme();
}

export function applyTheme(t: Theme): void {
  document.documentElement.setAttribute("data-theme", t);
  // Update the meta theme-color so iOS status bar / Android nav match
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "light" ? "#F7FAFA" : "#060d0f");
}

export function setTheme(t: Theme): void {
  try { localStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
  applyTheme(t);
}

export function toggleTheme(current: Theme): Theme {
  const next: Theme = current === "light" ? "dark" : "light";
  setTheme(next);
  return next;
}
